/**
 * LiveQRCode Component
 * Renders a real-time QR code that updates when data changes
 */
import { useEffect, useState, useRef } from 'react';
import { generateVCard } from '@/lib/vCardGenerator';
import { generateQRCodeDataURL } from '@/lib/qrCodeRenderer';
import { QrCode, Loader2 } from 'lucide-react';

interface LiveQRCodeProps {
  formData: Record<string, string>;
  templateName?: string;
  photoUrl?: string;
  className?: string;
}

export function LiveQRCode({ formData, templateName, photoUrl, className }: LiveQRCodeProps) {
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;

    async function generateQR() {
      try {
        setIsLoading(true);
        setError(null);

        // Generate vCard from form data with template name and photo URL
        const vcard = generateVCard(formData, templateName, photoUrl);
        
        // Generate QR code as data URL (black on transparent)
        const dataUrl = await generateQRCodeDataURL(vcard, {
          size: 256, // Good quality for preview
          darkColor: '#000000',
          lightColor: '#00000000', // Transparent
          margin: 0,
          errorCorrectionLevel: 'M',
        });

        if (!cancelled) {
          setQrDataUrl(dataUrl);
          setIsLoading(false);
        }
      } catch (err) {
        console.error('Failed to generate QR code:', err);
        if (!cancelled) {
          setError('QR generation failed');
          setIsLoading(false);
        }
      }
    }

    generateQR();

    return () => {
      cancelled = true;
    };
  }, [formData, templateName, photoUrl]);

  return (
    <div 
      ref={containerRef}
      className={`w-full h-full flex items-center justify-center ${className || ''}`}
    >
      {isLoading ? (
        <div className="flex items-center justify-center bg-white/80 w-full h-full">
          <Loader2 className="w-1/2 h-1/2 text-gray-400 animate-spin" />
        </div>
      ) : error ? (
        <div className="flex items-center justify-center bg-red-50 w-full h-full">
          <QrCode className="w-1/2 h-1/2 text-red-400" />
        </div>
      ) : qrDataUrl ? (
        <img 
          src={qrDataUrl} 
          alt="QR Code (vCard)" 
          className="w-full h-full object-contain"
          style={{ imageRendering: 'pixelated' }}
        />
      ) : (
        <div className="flex items-center justify-center bg-white/80 w-full h-full">
          <QrCode className="w-1/2 h-1/2 text-gray-400" />
        </div>
      )}
    </div>
  );
}
